import React from 'react'
import { theme } from '../theme'

/**
 * Transmission-quality panel for the run detail page.
 *
 * Shows the composite, the four subscores, and the raw physical measurements behind
 * them. The raw values are deliberately visible rather than tucked away: a subscore is
 * a judgement and the measurement is the evidence. "resolution 62" is arguable;
 * "resolved 50 lp/in horizontally, 25 vertically" is checkable.
 *
 * A missing score is NOT rendered as a blank. There are several innocent reasons a run
 * has none — the job has deep quality checking off, the run predates the feature, or no
 * calibration target was found on the received copy — and they need different responses
 * from whoever is looking. The backend supplies the reason and this displays it.
 */

type Detail = {
  scored?: boolean
  reason?: string
  composite?: number
  resolution?: number
  geometry?: number
  noise?: number
  tone?: number
  weights?: Record<string, number>
  registration?: Record<string, unknown>
  measurements?: Record<string, Record<string, unknown>>
  notes?: string[]
  target_page_index?: number
  pages_searched?: number
}

function scoreColour(v: number | undefined): string {
  if (typeof v !== 'number') return theme.text
  if (v >= 85) return '#4caf50'
  if (v >= 65) return '#ffa726'
  return '#f44336'
}

function Bar({ label, value, weight }: { label: string; value?: number; weight?: number }) {
  const v = typeof value === 'number' ? value : 0
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 52px', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem' }}>
      <span style={{ color: '#9aa0a6' }}>
        {label}
        {typeof weight === 'number' && (
          <span style={{ fontSize: '0.68rem', color: '#666' }}> {Math.round(weight * 100)}%</span>
        )}
      </span>
      <span style={{ background: theme.bgElevated, borderRadius: '3px', height: '8px', overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${Math.max(0, Math.min(100, v))}%`, height: '100%', background: scoreColour(value) }} />
      </span>
      <span style={{ textAlign: 'right', color: scoreColour(value), fontWeight: 600 }}>
        {typeof value === 'number' ? value.toFixed(1) : '—'}
      </span>
    </div>
  )
}

export function QualityScorePanel({
  score,
  detail,
  runId,
  canRescore,
  onRescored,
}: {
  score?: number | null
  detail?: Detail | null
  runId?: string
  canRescore?: boolean
  onRescored?: () => void
}) {
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)

  async function rescore() {
    if (!runId) return
    setBusy(true)
    setErr(null)
    try {
      const r = await fetch(`/api/runs/${runId}/rescore-quality`, { method: 'POST' })
      if (!r.ok) {
        const body = await r.json().catch(() => ({}))
        throw new Error(body.detail || `HTTP ${r.status}`)
      }
      onRescored?.()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const rescoreButton = canRescore && runId ? (
    <div
      style={{
        marginTop: '0.9rem',
        paddingTop: '0.8rem',
        borderTop: `1px solid ${theme.border}`,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.8rem',
        flexWrap: 'wrap',
      }}
    >
      <button
        onClick={rescore}
        disabled={busy}
        style={{
          padding: '0.55rem 1.1rem',
          background: busy ? theme.primaryDark : theme.primary,
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: busy ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {busy ? 'Measuring…' : 'Re-measure quality'}
      </button>
      <div style={{ fontSize: '0.75rem', color: '#9aa0a6', flex: 1, minWidth: '15rem' }}>
        Re-reads the received fax held in S3 and scores it again. Use it after a scorer
        fix, or for a run sent before scoring existed — available while the fax is inside
        its 30-day retention.
        {err && (
          <div style={{ color: '#ef9a9a', marginTop: '0.35rem', fontWeight: 600 }}>{err}</div>
        )}
      </div>
    </div>
  ) : null

  // Not scored. Say WHY — a bare blank makes "feature disabled" look identical to
  // "measurement failed", and those need different responses.
  //
  // The fallback deliberately does NOT assert a cause. It previously claimed the job
  // had deep checking disabled, which misdiagnosed a real bug in the opposite
  // direction: a fax that HAD carried a target went unscored, and the UI confidently
  // reported the feature was off. Only the backend knows which case applies, so when it
  // supplies no reason, say that rather than inventing one.
  if (typeof score !== 'number' || !detail?.scored) {
    const reason =
      detail?.reason ||
      'No quality measurement was recorded for this run. Either deep quality checking was off for the job when this fax was sent, or the received copy carried no calibration target page.'
    return (
      <div style={{ background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '0.85rem' }}>
        <div style={{ fontSize: '0.8rem', color: '#9aa0a6' }}>Not scored</div>
        <div style={{ fontSize: '0.78rem', marginTop: '0.35rem' }}>{reason}</div>
        {rescoreButton}
      </div>
    )
  }

  const m = detail.measurements || {}
  const res = Object.entries(m).filter(([, v]) => v.kind === 'resolution')
  const limit = (axis: string) =>
    res
      .filter(([, v]) => v.axis === axis && v.resolved)
      .reduce((best, [, v]) => Math.max(best, Number(v.lp_per_inch) || 0), 0)

  const white = m.solid_white as Record<string, number> | undefined
  const black = m.solid_black as Record<string, number> | undefined
  const rings = m.rings as Record<string, number> | undefined
  const gaps = Object.values(m)
    .filter(v => v.kind === 'continuity')
    .map(v => Number(v.longest_gap_px) || 0)
  const reg = (detail.registration || {}) as Record<string, number>

  return (
    <div style={{ background: theme.bgElevated, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '0.85rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.7rem' }}>
        <span style={{ fontSize: '1.7rem', fontWeight: 700, color: scoreColour(score) }}>{score.toFixed(1)}</span>
        <span style={{ fontSize: '0.75rem', color: '#9aa0a6' }}>composite / 100</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        <Bar label="Resolution" value={detail.resolution} weight={detail.weights?.resolution} />
        <Bar label="Noise" value={detail.noise} weight={detail.weights?.noise} />
        <Bar label="Geometry" value={detail.geometry} weight={detail.weights?.geometry} />
        <Bar label="Tone" value={detail.tone} weight={detail.weights?.tone} />
      </div>

      {/* The raw measurements. These are the evidence behind the bars above. */}
      <div style={{ marginTop: '0.85rem', paddingTop: '0.7rem', borderTop: `1px solid ${theme.border}`, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 0.7rem', fontSize: '0.75rem' }}>
        <strong style={{ color: '#9aa0a6' }}>Resolved</strong>
        <span>
          {limit('horizontal')} lp/in horizontal · {limit('vertical')} lp/in vertical
          <span style={{ color: '#666' }}> (fax is anisotropic; a difference is normal)</span>
        </span>

        <strong style={{ color: '#9aa0a6' }}>Skew</strong>
        <span>{typeof reg.skew_degrees === 'number' ? `${reg.skew_degrees.toFixed(2)}°` : '—'}</span>

        <strong style={{ color: '#9aa0a6' }}>Scale</strong>
        <span>
          {typeof reg.scale_x === 'number' ? reg.scale_x.toFixed(3) : '—'} ×{' '}
          {typeof reg.scale_y === 'number' ? reg.scale_y.toFixed(3) : '—'}
        </span>

        <strong style={{ color: '#9aa0a6' }}>Roundness</strong>
        <span>{typeof rings?.roundness === 'number' ? rings.roundness.toFixed(3) : '—'}<span style={{ color: '#666' }}> (1.000 = no distortion)</span></span>

        <strong style={{ color: '#9aa0a6' }}>Speckle</strong>
        <span>{typeof white?.speckle_fraction === 'number' ? `${(white.speckle_fraction * 100).toFixed(2)}%` : '—'}<span style={{ color: '#666' }}> ink where none was sent</span></span>

        <strong style={{ color: '#9aa0a6' }}>Dropout</strong>
        <span>{typeof black?.dropout_fraction === 'number' ? `${(black.dropout_fraction * 100).toFixed(2)}%` : '—'}<span style={{ color: '#666' }}> gaps in solid ink</span></span>

        <strong style={{ color: '#9aa0a6' }}>Worst line break</strong>
        <span>{gaps.length ? `${Math.max(...gaps)} px` : '—'}</span>

        {typeof detail.target_page_index === 'number' && (
          <>
            <strong style={{ color: '#9aa0a6' }}>Target found on</strong>
            <span>
              page {detail.target_page_index + 1} of the received fax
              <span style={{ color: '#666' }}>
                {' '}(the page is searched for, not assumed — the eAPI and SMTP delivery
                paths place it differently)
              </span>
            </span>
          </>
        )}
      </div>

      {detail.notes && detail.notes.length > 0 && (
        <ul style={{ margin: '0.8rem 0 0', paddingLeft: '1.1rem', fontSize: '0.75rem', color: '#ffa726' }}>
          {detail.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      {/* Also offered on a SCORED run, not only an unscored one. Re-measuring a run that
          already has a score is exactly what is needed after a scorer fix — and the first
          such fix arrived immediately: the target's page index was assumed rather than
          searched for, so every SMTP-delivered run needed re-measuring. */}
      {rescoreButton}

      <details style={{ marginTop: '0.8rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.75rem', color: '#9aa0a6' }}>
          All raw measurements
        </summary>
        <pre style={{ background: theme.bgAlt, border: `1px solid ${theme.border}`, borderRadius: '4px', padding: '0.6rem', fontSize: '0.68rem', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: '0.4rem' }}>
          {JSON.stringify({ registration: detail.registration, measurements: m, weights: detail.weights }, null, 2)}
        </pre>
      </details>
    </div>
  )
}
